import axios from 'axios';

// Use the gateway URL (port 9200) instead of direct stream service access
const API_BASE_URL = 'https://corearena.tv/api/stream';

/**
 * Get public replays with pagination (all or by gameId)
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Items per page (default 12)
 * @param {string} gameId - Optional game ID to filter by
 * @returns {Promise} - Returns { replays, pagination }
 */
export const getPublicReplays = async (page = 1, limit = 12, gameId = null) => {
  try {
    const params = { page, limit };
    if (gameId) {
      params.gameId = gameId;
    }
    const response = await axios.get(`${API_BASE_URL}/replays`, {
      params,
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching public replays:', error);
    throw error;
  }
};

/**
 * Get current user's replays with pagination
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Items per page (default 10)
 * @returns {Promise} - Returns { replays, pagination }
 */
export const getMyReplays = async (page = 1, limit = 10) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/my-replays`, {
      params: { page, limit },
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching replays:', error);
    throw error;
  }
};

/**
 * Get a user's public replays by username with pagination
 * @param {string} username - Channel username
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Items per page (default 10)
 * @returns {Promise} - Returns { replays, pagination }
 */
export const getUserReplaysByUsername = async (username, page = 1, limit = 10) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/replays/by-username/${encodeURIComponent(username)}`, {
      params: { page, limit },
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user public replays:', error);
    throw error;
  }
};

/**
 * Get specific replay details
 * @param {string} replayId - Replay ID
 * @returns {Promise} - Returns replay details
 */
export const getReplayDetails = async (replayId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/replays/${replayId}`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching replay details:', error);
    throw error;
  }
};

/**
 * Delete a replay (soft delete)
 * @param {string} replayId - Replay ID
 * @param {boolean} hardDelete - Whether to also delete the file (default false)
 * @returns {Promise} - Returns success message
 */
export const deleteReplay = async (replayId, hardDelete = false) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/replays/${replayId}`, {
      params: { hard: hardDelete },
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting replay:', error);
    throw error;
  }
};

/**
 * Format duration from seconds to HH:MM:SS or MM:SS
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted duration
 */
export const formatDuration = (seconds) => {
  if (!seconds) return '00:00';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format file size from bytes to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size (e.g., "1.5 GB", "250 MB")
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  
  if (i === 0) return `${bytes} ${sizes[i]}`;
  
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

/**
 * Format date to relative time (e.g., "2 hours ago", "3 days ago")
 * @param {string|Date} date - Date to format
 * @returns {string} - Relative time string
 */
export const formatRelativeTime = (date) => {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months !== 1 ? 's' : ''} ago`;
  }
  const years = Math.floor(diffDays / 365);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
};

/**
 * Get replay playback URL
 * @param {string} filePath - Replay file path from database
 * @returns {string} - Full playback URL
 */
export const getReplayPlaybackUrl = (filePath) => {
  const baseUrl = process.env.REACT_APP_RECORDINGS_BASE_URL || 'https://corearena.tv';
  return `${baseUrl}${filePath}`;
};

/**
 * Get replay thumbnail URL
 * @param {string} thumbnailPath - Thumbnail path from database
 * @returns {string} - Full thumbnail URL or placeholder
 */
export const getReplayThumbnailUrl = (thumbnailPath) => {
  if (!thumbnailPath) {
    return 'https://via.placeholder.com/640x360/1a1a2e/ffffff?text=No+Thumbnail';
  }
  const baseUrl = process.env.REACT_APP_RECORDINGS_BASE_URL || 'https://corearena.tv';
  return `${baseUrl}${thumbnailPath}`;
};

const replaysApi = {
  getPublicReplays,
  getMyReplays,
  getUserReplaysByUsername,
  getReplayDetails,
  deleteReplay,
  formatDuration,
  formatFileSize,
  formatRelativeTime,
  getReplayPlaybackUrl,
  getReplayThumbnailUrl
};

export default replaysApi;

